import { Helmet } from "react-helmet-async";

const SITE = "https://groundedtheapp.com";

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;
  /** Only pass these alongside `image`; a wrong size is worse than none. */
  imageWidth?: number;
  imageHeight?: number;
  imageType?: string;
  jsonLd?: Record<string, unknown>[];
}

/**
 * Per-route SEO tags. Sets unique <title>, description, canonical, and og:* —
 * overriding the static defaults in index.html for JS-executing crawlers.
 */
export function SEO({ title, description, path, image, type = "website", noindex, imageWidth, imageHeight, imageType, jsonLd }: SEOProps) {
  const url = path ? `${SITE}${path.startsWith("/") ? path : `/${path}`}` : SITE;
  const ogImage = image || `${SITE}/og-launch.jpg`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Grounded" />
      <meta property="og:image" content={ogImage} />
      {/* Scrapers that know the size up front render the large card immediately
          instead of downloading and measuring first. Emitted only when the
          caller actually knows the dimensions of the image it passed. */}
      {imageWidth ? <meta property="og:image:width" content={String(imageWidth)} /> : null}
      {imageHeight ? <meta property="og:image:height" content={String(imageHeight)} /> : null}
      {imageType ? <meta property="og:image:type" content={imageType} /> : null}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {jsonLd?.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
